import { createContext, useContext, useRef, type ReactNode } from 'react';
import { VisionManager } from '@/lib/vision-manager';

// Create a context for the manager
const VisionManagerContext = createContext<VisionManager | null>(null);
const defaultVisionManager = new VisionManager();

type VisionManagerProviderProps = {
    children: ReactNode;
    /** Optional override for supplying a custom manager instance. */
    manager?: VisionManager;
};

/**
 * Provides a single VisionManager instance to the entire application.
 * This manager is responsible for creating and destroying all VisionClient instances.
 */
export const VisionManagerProvider = ({ children, manager }: VisionManagerProviderProps) => {
    // Use useRef to ensure the manager is only created once for this provider.
    const managerRef = useRef<VisionManager | null>(null);
    if (!managerRef.current) {
        managerRef.current = manager ?? new VisionManager();
    } else if (manager && managerRef.current !== manager) {
        managerRef.current = manager;
    }

    return (
        <VisionManagerContext.Provider value={managerRef.current}>
            {children}
        </VisionManagerContext.Provider>
    );
};

/**
 * Custom hook to access the global VisionManager.
 */
export const useVisionManager = (): VisionManager => {
    const context = useContext(VisionManagerContext);
    return context ?? defaultVisionManager;
};
